import { FC, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { useLocale, useTranslations } from 'next-intl'
import clsx from 'clsx'
import useSWR from 'swr'
import { DatePicker } from 'antd'
import {
  getPhoneAreaCodeOptionsMock,
  PhoneAreaCodeOption,
  SubmitIssueFeedbackPayload,
} from '@/service/fault-reporting.service'
import { CountryList } from '@/common/country-list'
import {
  IssueAttachmentItem,
  IssueAttachmentUpload,
} from '@/components/common/issue-attachment-upload'
import { WarrantyReportInfoCard } from './warranty-report-info-card'

interface DateValueLike {
  valueOf: () => number
}

export interface IssuePrefillValues {
  errorCodes?: string
  errorDescs?: string
  logDirs?: string
  logUrl?: string
}

export interface WarrantyReportFormProps {
  sn: string
  mac?: string
  productName?: string
  productImage?: string
  initialValues?: IssuePrefillValues
  onSubmit: (data: SubmitIssueFeedbackPayload) => void
  onSendEmailCode: (email: string) => Promise<boolean>
  onResetInput: () => void
  isLoading?: boolean
}

interface FormValues {
  sn: string
  mac: string
  errorCodes: string
  errorDescs: string
  faultTime: DateValueLike | null
  logDirs: string
  logUrl: string
  contactName: string
  contactGender: 'male' | 'female'
  email: string
  code: string
  contactPhoneCode: string
  phone: string
  contactCountryCrmId: string
}

const snRegex = /^[0-9a-zA-Z]{13,16}$/
const macRegex = /^[a-fA-F0-9:]{17}$/
const emailRegex =
  /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+$/

const schema = Yup.object({
  sn: Yup.string()
    .required('required')
    .test('sn', 'invalid-sn', (value) => !!value && snRegex.test(value.trim())),
  mac: Yup.string()
    .required('required')
    .test('mac', 'invalid-mac', (value) => !!value && macRegex.test(value.trim())),
  // 故障描述最多 200 字符（与后端/交互约定一致）
  errorDescs: Yup.string().required('required').max(200, 'max-error-desc'),
  faultTime: Yup.mixed<DateValueLike>().required('required'),
  email: Yup.string()
    .required('required')
    .test('email', 'invalid-email', (value) => !!value && emailRegex.test(value.trim())),
  code: Yup.string().required('required'),
  contactPhoneCode: Yup.string().required('required'),
  phone: Yup.string()
    .required('required')
    .test('phone', 'invalid-phone', (value) => {
      if (!value) {
        return false
      }
      const normalizedPhone = value.replace(/\s+/g, '').trim()
      return /^[0-9]{1,20}$/.test(normalizedPhone)
    }),
  contactCountryCrmId: Yup.string().required('required'),
  contactName: Yup.string().required('required'),
  contactGender: Yup.mixed<'male' | 'female'>().oneOf(['male', 'female']).required('required'),
})

const getAllCountries = () => {
  const allCountries: Array<{ name: string; code: string; area_code: string }> = []
  Object.values(CountryList).forEach((region) => {
    allCountries.push(...region)
  })
  return allCountries
}

const WarrantyReportForm: FC<WarrantyReportFormProps> = ({
  sn,
  mac,
  productName,
  productImage,
  initialValues,
  onSubmit,
  onSendEmailCode,
  onResetInput,
  isLoading = false,
}) => {
  const t = useTranslations()
  const locale = useLocale()
  const countries = useMemo(() => getAllCountries(), [])
  const { data: phoneAreaCodes } = useSWR<PhoneAreaCodeOption[]>(
    getPhoneAreaCodeOptionsMock.swrkey(locale),
    () => getPhoneAreaCodeOptionsMock(),
    {
      fallbackData: [{ code: '+86', label: '+86' }],
    }
  )
  const [attachments, setAttachments] = useState<IssueAttachmentItem[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [submitUploadError, setSubmitUploadError] = useState<string | null>(null)
  const [sendCodeLoading, setSendCodeLoading] = useState(false)
  const [cooldown, setCooldown] = useState(0)

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      sn,
      mac: mac || '',
      errorCodes: initialValues?.errorCodes || '',
      errorDescs: initialValues?.errorDescs || '',
      faultTime: null,
      logDirs: initialValues?.logDirs || '',
      logUrl: initialValues?.logUrl || '',
      contactGender: 'male',
      contactName: '',
      email: '',
      code: '',
      contactPhoneCode: '+86',
      phone: '',
      contactCountryCrmId: '',
    },
  })

  const email = watch('email')
  const logPathValue = watch('logDirs')
  const errorCodeValue = watch('errorCodes')
  const logUrlValue = watch('logUrl')

  useEffect(() => {
    reset({
      sn,
      mac: mac || '',
      errorCodes: initialValues?.errorCodes || '',
      errorDescs: initialValues?.errorDescs || '',
      faultTime: null,
      logDirs: initialValues?.logDirs || '',
      logUrl: initialValues?.logUrl || '',
      contactGender: 'male',
      contactName: '',
      email: '',
      code: '',
      contactPhoneCode: '+86',
      phone: '',
      contactCountryCrmId: '',
    })
  }, [
    initialValues?.errorCodes,
    initialValues?.errorDescs,
    initialValues?.logDirs,
    initialValues?.logUrl,
    mac,
    reset,
    sn,
  ])

  const handleSendCode = async () => {
    if (sendCodeLoading || cooldown > 0 || !emailRegex.test((email || '').trim())) {
      return
    }
    setSendCodeLoading(true)
    const success = await onSendEmailCode(email.trim())
    setSendCodeLoading(false)
    if (success) {
      setCooldown(59)
    }
  }

  useEffect(() => {
    if (cooldown <= 0) {
      return
    }
    const timer = window.setTimeout(() => {
      setCooldown((prev) => prev - 1)
    }, 1000)
    return () => window.clearTimeout(timer)
  }, [cooldown])

  const onFormSubmit = (data: FormValues) => {
    if (isUploading) {
      setSubmitUploadError(t('FaultReportingForm.UploadingInProgress'))
      return
    }
    setSubmitUploadError(null)

    const submitData: SubmitIssueFeedbackPayload = {
      sn: data.sn.trim(),
      mac: data.mac.trim(),
      errorType: data.errorCodes?.trim() || '',
      errorDesc: data.errorDescs.trim(),
      faultTime: data.faultTime?.valueOf ? data.faultTime.valueOf() : 0,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      logPath: data.logDirs?.trim() || '',
      logUrl: data.logUrl?.trim() || '',
      attachUrl: attachments
        .filter((item) => item.status === 'done' && item.url)
        .map((item) => ({
          attach_url: item.url as string,
          attach_type: item.ext || 'unknown',
        })),
      contactInfo: `${data.contactName.trim()} ${data.contactGender === 'male' ? t('FaultReportingForm.Mr') : t('FaultReportingForm.Ms')}`.trim(),
      email: data.email.trim(),
      emailVerificationCode: data.code.trim(),
      phone: `${data.contactPhoneCode}-${data.phone.replace(/\s+/g, '').trim()}`,
      contactCountryCrmId: data.contactCountryCrmId,
    }
    onSubmit(submitData)
  }

  return (
    <div className={clsx('w-full')}>
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className={clsx(
          'w-full laptop:max-w-[795px]',
          'bg-white/90',
          'backdrop-blur-[48px]',
          'rounded-[8px]',
          'p-0 laptop:p-0',
          'flex flex-col',
          'gap-6 laptop:gap-10'
        )}
      >
        <p
          className={clsx(
            'text-14 laptop:text-14 font-semibold',
            'text-black/60',
            'leading-[22px]'
          )}
        >
          {t('FaultReportingForm.FormDesc')}
        </p>

        <div className={clsx('flex flex-col', 'gap-6 laptop:gap-10')}>
          <WarrantyReportInfoCard
            sn={sn}
            mac={mac}
            productName={productName}
            productImage={productImage}
            onResetInput={onResetInput}
          />

          <div className={clsx('hidden')}>
            <input {...register('sn')} />
            <input {...register('mac')} />
          </div>

          {(errors.sn || errors.mac) && (
            <p className={clsx('text-16 laptop:text-16 text-[#F52242]')}>
              {errors.sn ? t('FaultReportingForm.InvalidSN') : t('FaultReportingForm.InvalidMAC')}
            </p>
          )}

          <div className={clsx('hidden')}>
            <input {...register('logUrl')} />
          </div>

          <div className={clsx('hidden')}>
            <input {...register('errorCodes')} />
            <input {...register('logDirs')} />
          </div>

          {!!errorCodeValue && (
            <div className={clsx('flex flex-col', 'gap-2')}>
              <label className={clsx('text-16 laptop:text-14 font-semibold', 'text-black/90', 'leading-6 laptop:leading-[22px]')}>
                {t('FaultReportingForm.ErrorCode')}
              </label>
              <div
                className={clsx(
                  'w-full min-h-10 bg-pdbg rounded-[2px] px-4 py-[9px]',
                  'text-14 text-black/90 break-all'
                )}
              >
                {errorCodeValue}
              </div>
            </div>
          )}

          {!!(logPathValue || logUrlValue) && (
            <div className={clsx('flex flex-col', 'gap-2')}>
              <label className={clsx('text-16 laptop:text-14 font-semibold', 'text-black/90', 'leading-6 laptop:leading-[22px]')}>
                {t('FaultReportingForm.LogPath')}
              </label>
              <div
                className={clsx(
                  'w-full min-h-10 bg-pdbg rounded-[2px] px-4 py-[9px]',
                  'text-14 text-black/90 break-all'
                )}
              >
                {logPathValue || logUrlValue}
              </div>
            </div>
          )}

          <div className={clsx('flex flex-col', 'laptop:flex-row laptop:justify-between', 'gap-4 laptop:gap-0')}>
            <div className={clsx('flex flex-col', 'gap-2', 'laptop:w-[390px]')}>
              <label className={clsx('text-16 laptop:text-14 font-semibold text-black/90', 'leading-6 laptop:leading-[22px]')}>
                {t('FaultReportingForm.ErrorDesc')} <span className={clsx('text-[#F52242]')}>*</span>
              </label>
              <textarea
                {...register('errorDescs')}
                rows={5}
                maxLength={200}
                className={clsx(
                  'w-full h-[160px] laptop:h-[110px] bg-pdbg rounded-[2px] px-3 laptop:px-4 py-2.5 laptop:py-3 text-14 text-black/90 placeholder:text-black/40 outline-none border-none resize-none'
                )}
                placeholder={t('FaultReportingForm.EnterErrorDesc')}
              />
              {errors.errorDescs && (
                <p className={clsx('text-16 laptop:text-16 text-[#F52242]')}>
                  {errors.errorDescs.message === 'max-error-desc'
                    ? t('FaultReportingForm.ErrorDescMaxLength')
                    : t('FaultReportingForm.Required')}
                </p>
              )}
            </div>

            <div className={clsx('flex flex-col', 'gap-2', 'laptop:w-[390px]')}>
              <label className={clsx('text-16 laptop:text-14 font-semibold text-black/90', 'leading-6 laptop:leading-[22px]')}>
                {t('FaultReportingForm.FaultTime')} <span className={clsx('text-[#F52242]')}>*</span>
              </label>
              <Controller
                name="faultTime"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    value={field.value as any}
                    onChange={(value) => field.onChange(value)}
                    showTime={{ format: 'HH:mm' }}
                    format="YYYY-MM-DD HH:mm"
                    placeholder={t('FaultReportingForm.EnterFaultTime')}
                    className={clsx(
                      '!w-full !h-10',
                      '[&_.ant-picker-input>input]:!text-14',
                      '[&_.ant-picker-input>input]:!text-black/90',
                      '[&_.ant-picker-input>input]:!placeholder:text-black/40',
                      '[&_.ant-picker-input>input]:!h-10',
                      '[&_.ant-picker-input>input]:!leading-[22px]',
                      '[&_.ant-picker-input>input]:!px-0',
                      '[&_.ant-picker-suffix]:!text-black/40',
                      '!bg-pdbg !rounded-[2px] !px-3 laptop:!px-4 !border !border-transparent',
                      'hover:!border-transparent',
                      '[&.ant-picker-focused]:!border-pdblue',
                      '[&.ant-picker-focused]:!shadow-none'
                    )}
                  />
                )}
              />
              {errors.faultTime && (
                <p className={clsx('text-16 laptop:text-16 text-[#F52242]')}>{t('FaultReportingForm.Required')}</p>
              )}
            </div>
          </div>

          <div className={clsx('flex flex-col', 'gap-2')}>
            <IssueAttachmentUpload
              locale={locale}
              disabled={isLoading}
              onChange={(items) => {
                setAttachments(items)
                if (submitUploadError)
                  setSubmitUploadError(null)
              }}
              onUploadingChange={(uploading) => {
                setIsUploading(uploading)
                if (!uploading && submitUploadError)
                  setSubmitUploadError(null)
              }}
            />
            {submitUploadError && <p className={clsx('text-14', 'text-[#F52242]')}>{submitUploadError}</p>}
          </div>

          <div className={clsx('flex flex-col', 'laptop:flex-row laptop:justify-between', 'gap-4 laptop:gap-0')}>
            <div className={clsx('flex flex-col', 'gap-[10px]', 'laptop:w-[390px]')}>
              <label className={clsx('h-6 laptop:h-[22px]', 'inline-flex', 'items-center', 'gap-1', 'text-16 laptop:text-14 font-semibold text-black/90')}>
                <span>{t('FaultReportingForm.ContactName')}</span>
                <span className={clsx('inline-flex', 'w-4 h-4', 'items-center justify-center', 'text-[#F52242]', 'text-14', 'leading-none')}>
                  *
                </span>
              </label>
              <div className={clsx('flex', 'flex-col laptop:flex-row', 'gap-4 laptop:gap-1', 'items-start', 'w-full')}>
                <input
                  {...register('contactName')}
                  type="text"
                  placeholder={t('FaultReportingForm.EnterContactName')}
                  className={clsx(
                    'h-10',
                    'w-full laptop:flex-none laptop:w-[230px]',
                    'bg-pdbg rounded-[2px] px-3 laptop:px-4',
                    'text-14 text-black/90 placeholder:text-black/40',
                    'outline-none border-none'
                  )}
                />
                <Controller
                  name="contactGender"
                  control={control}
                  render={({ field }) => (
                    <div className={clsx('flex', 'gap-2 laptop:gap-1', 'w-[112px] laptop:w-[156px]')}>
                      <button
                        type="button"
                        onClick={() => field.onChange('male')}
                        className={clsx(
                          'w-[52px] laptop:w-[76px] h-[29px] laptop:h-10 rounded-[2px] text-14 leading-[21px] laptop:leading-[22px]',
                          field.value === 'male'
                            ? 'bg-[#DBEAFF] border border-pdblue text-pdblue'
                            : 'bg-pdbg laptop:bg-white border border-[#BBC3CE] text-black/40'
                        )}
                      >
                        {t('FaultReportingForm.Mr')}
                      </button>
                      <button
                        type="button"
                        onClick={() => field.onChange('female')}
                        className={clsx(
                          'w-[52px] laptop:w-[76px] h-[29px] laptop:h-10 rounded-[2px] text-14 leading-[21px] laptop:leading-[22px]',
                          field.value === 'female'
                            ? 'bg-[#DBEAFF] border border-pdblue text-pdblue'
                            : 'bg-pdbg laptop:bg-white border border-[#BBC3CE] text-black/40'
                        )}
                      >
                        {t('FaultReportingForm.Ms')}
                      </button>
                    </div>
                  )}
                />
              </div>
              {errors.contactName && (
                <p className={clsx('text-16 laptop:text-16 text-[#F52242]')}>{t('FaultReportingForm.Required')}</p>
              )}
            </div>

            <div className={clsx('flex flex-col', 'gap-2', 'laptop:w-[390px]')}>
              <label className={clsx('text-16 laptop:text-14 font-semibold text-black/90', 'leading-6 laptop:leading-[22px]')}>
                {t('FaultReportingForm.Phone')} <span className={clsx('text-[#F52242]')}>*</span>
              </label>
              <div
                className={clsx(
                  'w-full h-10 bg-pdbg rounded-[2px] px-3 laptop:px-4 border border-transparent',
                  'focus-within:border-pdblue',
                  'flex items-center'
                )}
              >
                <div className={clsx('relative', 'w-[62px]', 'h-10', 'flex-shrink-0')}>
                  <Controller
                    name="contactPhoneCode"
                    control={control}
                    render={({ field }) => (
                      <select
                        {...field}
                        className={clsx(
                          'w-full h-10 bg-transparent appearance-none',
                          'text-14 leading-[22px] text-black/60',
                          'outline-none border-none ring-0 shadow-none',
                          'focus:!outline-none focus:!border-none focus:!ring-0',
                          'focus-visible:!outline-none focus-visible:!ring-0',
                          'm-0 py-0 pl-0 pr-4'
                        )}
                        style={{
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          appearance: 'none',
                        }}
                      >
                        {(phoneAreaCodes || []).map((item) => (
                          <option key={item.code} value={item.code}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                </div>
                <div className={clsx('w-px h-6 bg-black/20', 'mx-3')} />
                <input
                  {...register('phone')}
                  type="tel"
                  placeholder={t('FaultReportingForm.EnterPhone')}
                  className={clsx(
                    'flex-1 h-full bg-transparent',
                    'text-14 text-black/90 placeholder:text-black/40',
                    'outline-none border-none ring-0 shadow-none',
                    'focus:!outline-none focus:!border-none focus:!ring-0',
                    'focus-visible:!outline-none focus-visible:!ring-0'
                  )}
                />
              </div>
              {errors.phone && (
                <p className={clsx('text-16 laptop:text-16 text-[#F52242]')}>{t('FaultReportingForm.InvalidPhone')}</p>
              )}
            </div>
          </div>

          <div className={clsx('grid grid-cols-1', 'laptop:grid-cols-2', 'gap-4')}>
            <div className={clsx('flex flex-col', 'gap-2')}>
              <label className={clsx('text-16 laptop:text-14 font-semibold text-black/90', 'leading-6 laptop:leading-[22px]')}>
                {t('FaultReportingForm.Email')} <span className={clsx('text-[#F52242]')}>*</span>
              </label>
              <input
                {...register('email')}
                type="email"
                placeholder={t('FaultReportingForm.EnterEmail')}
                className={clsx(
                  'w-full h-10 bg-pdbg rounded-[2px] px-3 laptop:px-4 text-14 text-black/90 placeholder:text-black/40 outline-none border-none'
                )}
              />
              {errors.email && (
                <p className={clsx('text-16 laptop:text-16 text-[#F52242]')}>{t('FaultReportingForm.InvalidEmail')}</p>
              )}

              <div className={clsx('flex', 'gap-2', 'items-start', 'w-full')}>
                <input
                  {...register('code')}
                  type="text"
                  placeholder={t('FaultReportingForm.EnterEmailCode')}
                  className={clsx(
                    'flex-1 h-10 bg-pdbg rounded-[2px] px-3 laptop:px-4 text-14 text-black/90 placeholder:text-black/40 outline-none border-none'
                  )}
                />
                <button
                  type="button"
                  onClick={handleSendCode}
                  disabled={sendCodeLoading || cooldown > 0 || !emailRegex.test((email || '').trim())}
                  className={clsx(
                    'h-10 w-[124px] bg-pdblue rounded-[2px] text-white text-14 font-semibold disabled:opacity-60'
                  )}
                >
                  {sendCodeLoading
                    ? t('FaultReportingForm.Sending')
                    : cooldown > 0
                      ? t('FaultReportingForm.Countdown', { second: cooldown })
                      : t('FaultReportingForm.SendCode')}
                </button>
              </div>
              {errors.code && <p className={clsx('text-16 laptop:text-16 text-[#F52242]')}>{t('FaultReportingForm.Required')}</p>}
            </div>

            <div className={clsx('flex flex-col', 'gap-2')}>
              <label className={clsx('text-16 laptop:text-14 font-semibold text-black/90', 'leading-6 laptop:leading-[22px]')}>
                {t('FaultReportingForm.CountryRegion')} <span className={clsx('text-[#F52242]')}>*</span>
              </label>
              <Controller
                name="contactCountryCrmId"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    className={clsx(
                      'w-full h-10 bg-pdbg rounded-[2px] px-3 laptop:px-4 text-14 text-black/90 outline-none border-none'
                    )}
                  >
                    <option value="">{t('FaultReportingForm.PleaseSelect')}</option>
                    {countries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.contactCountryCrmId && (
                <p className={clsx('text-16 laptop:text-16 text-[#F52242]')}>{t('FaultReportingForm.Required')}</p>
              )}
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading || isUploading}
          className={clsx(
            'w-full laptop:w-[124px]',
            'h-[50px] laptop:h-10',
            'bg-pdblue',
            'rounded-[2px]',
            'text-16 laptop:text-base font-semibold',
            'text-white',
            'leading-6 laptop:leading-[24px]',
            'hover:opacity-90',
            'transition-opacity',
            'disabled:opacity-50',
            'disabled:cursor-not-allowed'
          )}
        >
          {isLoading ? t('FaultReportingForm.Submitting') : t('FaultReportingForm.Submit')}
        </button>
      </form>
    </div>
  )
}

export default WarrantyReportForm
